import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { authHeaders } from "../utils/driverPrivacy";
import "../styles/PagesModern.css";
import { PageHeader, PanelHeader, SegmentedControl } from "./RefreshUI";

const API_BASE = "https://iracing6-backend.herokuapp.com/api";

export const TelemetryPage = () => {
        const navigate = useNavigate();
        const [car, setCar] = useState("");
        const [track, setTrack] = useState("");
        const [driverCustId, setDriverCustId] = useState("");
        const [driverName, setDriverName] = useState("");
        const [sessionType, setSessionType] = useState("");
        const [timeFrame, setTimeFrame] = useState("");
        const [data, setData] = useState(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null);

        // Pagination state
        const [currentSkip, setCurrentSkip] = useState(0);
        const [hasMore, setHasMore] = useState(false);
        const [loadingMore, setLoadingMore] = useState(false);
        const LIMIT = 20;

        // Autocomplete state
        const [listOfCars, setListOfCars] = useState([]);
        const [listOfTracks, setListOfTracks] = useState([]);
        const [carSearchTerm, setCarSearchTerm] = useState("");
        const [trackSearchTerm, setTrackSearchTerm] = useState("");
        const [selectedCar, setSelectedCar] = useState(null);
        const [selectedTrack, setSelectedTrack] = useState(null);
        const [myPrivateTelemetry, setMyPrivateTelemetry] = useState(false);

        // Comparison mode state
        const [comparisonMode, setComparisonMode] = useState('fastest'); // 'fastest' | 'select'
        const [selectedLaps, setSelectedLaps] = useState([]); // Array of {lap, session} objects (max 2)

        const isLoggedIn = !!localStorage.getItem("token");

        // Fetch cars and tracks for autocomplete
        useEffect(() => {
                const fetchCars = async () => {
                        try {
                                const response = await fetch(`${API_BASE}/car-basic-info/`);
                                const data = await response.json();
                                if (response.ok) {
                                        const sortedCars = data.sort((a, b) => a.car_name.localeCompare(b.car_name));
                                        setListOfCars(sortedCars);
                                }
                        } catch (err) {
                                console.error("Failed to fetch cars:", err);
                        }
                };

                const fetchTracks = async () => {
                        try {
                                const response = await fetch(`${API_BASE}/track-basic-info/`);
                                const data = await response.json();
                                if (response.ok) {
                                        const sortedTracks = data.sort((a, b) => a.track_name.localeCompare(b.track_name));
                                        setListOfTracks(sortedTracks);
                                }
                        } catch (err) {
                                console.error("Failed to fetch tracks:", err);
                        }
                };

                fetchCars();
                fetchTracks();
        }, []);

        const fetchTelemetry = async (skip = 0, append = false) => {
                if (append) {
                        setLoadingMore(true);
                } else {
                        setLoading(true);
                        setCurrentSkip(0);
                }
                setError(null);

                const params = new URLSearchParams();
                if (car) params.append("car", car);
                if (track) params.append("track", track);
                if (driverCustId) params.append("driverCustId", driverCustId);
                if (driverName) params.append("driverName", driverName);
                if (sessionType) params.append("sessionType", sessionType);
                if (timeFrame) params.append("timeFrame", timeFrame);
                if (myPrivateTelemetry) {
                        params.append("myPrivateTelemetry", "true");
                }

                // Add pagination params
                params.append("limit", LIMIT.toString());
                params.append("skip", skip.toString());

                if (!car && !track && !driverCustId && !driverName && !sessionType && !timeFrame && !myPrivateTelemetry) {
                        setError("At least one query parameter is required");
                        setLoading(false);
                        setLoadingMore(false);
                        return;
                }

                const url = `${API_BASE}/telemetry/optimized?${params.toString()}`;

                try {
                        const response = await axios.get(url, { headers: authHeaders() });
                        const newData = response.data;

                        if (append && data) {
                                // Append new sessions to existing data
                                setData({
                                        ...newData,
                                        sessions: [...data.sessions, ...newData.sessions],
                                });
                        } else {
                                setData(newData);
                        }

                        // Update pagination state
                        setHasMore(newData.pagination?.hasMore || false);
                        setCurrentSkip(skip);
                } catch (err) {
                        console.error("Telemetry error:", err);
                        setError(err.response?.data?.message || err.message);
                } finally {
                        setLoading(false);
                        setLoadingMore(false);
                }
        };

        const handleSubmit = (e) => {
                e.preventDefault();
                fetchTelemetry(0, false);
        };

        const handleLoadMore = () => {
                const nextSkip = currentSkip + LIMIT;
                fetchTelemetry(nextSkip, true);
        };

        const handleLapClick = (lap, session) => {
                if (comparisonMode === 'fastest') {
                        // Existing behavior: navigate to compare with fastest
                        navigate("/telemetry/compare", {
                                state: { lap, session },
                        });
                } else {
                        // Select mode: add/remove lap from selection
                        const lapKey = `${lap.lapId || lap.lapNumber}-${session.car}-${session.track}`;
                        const isSelected = selectedLaps.some(
                                (s) => `${s.lap.lapId || s.lap.lapNumber}-${s.session.car}-${s.session.track}` === lapKey
                        );

                        if (isSelected) {
                                // Remove from selection
                                setSelectedLaps(selectedLaps.filter(
                                        (s) => `${s.lap.lapId || s.lap.lapNumber}-${s.session.car}-${s.session.track}` !== lapKey
                                ));
                        } else if (selectedLaps.length < 2) {
                                // Add to selection (max 2)
                                setSelectedLaps([...selectedLaps, { lap, session }]);
                        }
                }
        };

        const isLapSelected = (lap, session) => {
                const lapKey = `${lap.lapId || lap.lapNumber}-${session.car}-${session.track}`;
                return selectedLaps.some(
                        (s) => `${s.lap.lapId || s.lap.lapNumber}-${s.session.car}-${s.session.track}` === lapKey
                );
        };

        const getSelectedLapIndex = (lap, session) => {
                const lapKey = `${lap.lapId || lap.lapNumber}-${session.car}-${session.track}`;
                return selectedLaps.findIndex(
                        (s) => `${s.lap.lapId || s.lap.lapNumber}-${s.session.car}-${s.session.track}` === lapKey
                );
        };

        const handleCompareSelected = () => {
                if (selectedLaps.length === 2) {
                        navigate("/telemetry/compare", {
                                state: {
                                        lap: selectedLaps[0].lap,
                                        session: selectedLaps[0].session,
                                        lap2: selectedLaps[1].lap,
                                        session2: selectedLaps[1].session,
                                },
                        });
                }
        };

        const clearSelectedLaps = () => {
                setSelectedLaps([]);
        };

        const formatLapTime = (seconds) => {
                if (!seconds) return "--";
                const mins = Math.floor(seconds / 60);
                const secs = (seconds % 60).toFixed(3);
                return mins > 0 ? `${mins}:${secs.padStart(6, "0")}` : `${secs}s`;
        };

        // Car/track suggestions shown under the inputs while typing.
        const renderSuggestions = (term, items, nameKey, idKey, selected, onPick, emptyLabel) => {
                if (term.trim() === "") return null;
                const matches = items.filter((item) => item[nameKey].toLowerCase().includes(term.toLowerCase()));
                return (
                        <ul className="pg-suggest" role="listbox">
                                {matches.length === 0 ? (
                                        <li className="pg-suggest__empty">{emptyLabel}</li>
                                ) : (
                                        matches.slice(0, 12).map((item) => (
                                                <li key={item[idKey]}>
                                                        <button
                                                                type="button"
                                                                role="option"
                                                                aria-selected={selected?.[idKey] === item[idKey]}
                                                                className="pg-suggest__item"
                                                                onMouseDown={(e) => e.preventDefault()}
                                                                onClick={() => onPick(item)}
                                                        >
                                                                {item[nameKey]}
                                                        </button>
                                                </li>
                                        ))
                                )}
                                {matches.length > 12 && <li className="pg-suggest__empty">Keep typing to narrow {matches.length} matches</li>}
                        </ul>
                );
        };

        const hasQuery = !!(car || track || driverCustId || driverName || sessionType || timeFrame || myPrivateTelemetry);
        const lapHint = comparisonMode === "fastest" ? "Select a lap to compare it with the fastest lap for that car and track." : `Pick two laps to compare (${selectedLaps.length} of 2 selected).`;

        const renderSlot = (entry, label) => (
                <div className="pg-compare-slot">
                        <span className="pg-compare-slot__label">{label}</span>
                        {entry ? (
                                <>
                                        <span className="pg-compare-slot__time">{formatLapTime(entry.lap.lapTime)}</span>
                                        <span className="pg-compare-slot__info">
                                                {entry.session.car} at {entry.session.track}
                                        </span>
                                </>
                        ) : (
                                <span className="pg-compare-slot__info">Select a lap</span>
                        )}
                </div>
        );

        return (
                <div className="pg">
                        <main className="hp-main pg-main--telem">
                                <PageHeader title="Telemetry" lede="Find laps from shared telemetry and compare them against the fastest lap, or against each other.">
                                        <button type="button" className="pg-btn pg-btn--ghost" onClick={() => navigate("/telemetry/upload")}>
                                                Upload telemetry
                                        </button>
                                </PageHeader>

                                <form onSubmit={handleSubmit} className="hp-panel pg-telem-form">
                                        <PanelHeader title="Search laps" />
                                        <div className="pg-telem-grid">
                                                <div className="pg-field pg-telem-auto">
                                                        <label className="pg-field__label" htmlFor="telem-car">
                                                                Car
                                                        </label>
                                                        <input
                                                                id="telem-car"
                                                                type="text"
                                                                autoComplete="off"
                                                                className="pg-input pg-input--fill"
                                                                value={selectedCar ? selectedCar.car_name : carSearchTerm}
                                                                onChange={(e) => {
                                                                        setCarSearchTerm(e.target.value);
                                                                        setCar(e.target.value);
                                                                        setSelectedCar(null);
                                                                }}
                                                                onBlur={() => window.setTimeout(() => setCarSearchTerm((term) => (selectedCar ? "" : term)), 150)}
                                                                placeholder={"BMW, Ferrari, Mazda MX-5 Cup…"}
                                                        />
                                                        {renderSuggestions(carSearchTerm, listOfCars, "car_name", "car_id", selectedCar, (c) => {
                                                                setSelectedCar(c);
                                                                setCar(c.car_name);
                                                                setCarSearchTerm("");
                                                        }, "No cars match")}
                                                </div>
                                                <div className="pg-field pg-telem-auto">
                                                        <label className="pg-field__label" htmlFor="telem-track">
                                                                Track
                                                        </label>
                                                        <input
                                                                id="telem-track"
                                                                type="text"
                                                                autoComplete="off"
                                                                className="pg-input pg-input--fill"
                                                                value={selectedTrack ? selectedTrack.track_name : trackSearchTerm}
                                                                onChange={(e) => {
                                                                        setTrackSearchTerm(e.target.value);
                                                                        setTrack(e.target.value);
                                                                        setSelectedTrack(null);
                                                                }}
                                                                onBlur={() => window.setTimeout(() => setTrackSearchTerm((term) => (selectedTrack ? "" : term)), 150)}
                                                                placeholder={"Spa, Monza, Oulton Park…"}
                                                        />
                                                        {renderSuggestions(trackSearchTerm, listOfTracks, "track_name", "track_id", selectedTrack, (t) => {
                                                                setSelectedTrack(t);
                                                                setTrack(t.track_name);
                                                                setTrackSearchTerm("");
                                                        }, "No tracks match")}
                                                </div>
                                                <div className="pg-field">
                                                        <label className="pg-field__label" htmlFor="telem-cust">
                                                                Driver customer ID
                                                        </label>
                                                        <input id="telem-cust" type="text" inputMode="numeric" className="pg-input pg-input--fill" value={driverCustId} onChange={(e) => setDriverCustId(e.target.value)} placeholder="123456" />
                                                </div>
                                                <div className="pg-field">
                                                        <label className="pg-field__label" htmlFor="telem-driver">
                                                                Driver name
                                                        </label>
                                                        <input id="telem-driver" type="text" className="pg-input pg-input--fill" value={driverName} onChange={(e) => setDriverName(e.target.value)} placeholder="Part of a name" />
                                                </div>
                                                <div className="pg-field">
                                                        <label className="pg-field__label" htmlFor="telem-session">
                                                                Session
                                                        </label>
                                                        <select id="telem-session" className="pg-input pg-input--fill" value={sessionType} onChange={(e) => setSessionType(e.target.value)}>
                                                                <option value="">All sessions</option>
                                                                <option value="Practice">Practice</option>
                                                                <option value="Qualifying">Qualifying</option>
                                                                <option value="Race">Race</option>
                                                                <option value="Time Trial">Time trial</option>
                                                                <option value="Lone Qualifying">Lone qualifying</option>
                                                        </select>
                                                </div>
                                                <div className="pg-field">
                                                        <label className="pg-field__label" htmlFor="telem-time">
                                                                When
                                                        </label>
                                                        <select id="telem-time" className="pg-input pg-input--fill" value={timeFrame} onChange={(e) => setTimeFrame(e.target.value)}>
                                                                <option value="">All time</option>
                                                                <option value="24h">Last 24 hours</option>
                                                                <option value="7d">Last 7 days</option>
                                                                <option value="30d">Last 30 days</option>
                                                                <option value="90d">Last 90 days</option>
                                                                <option value="1y">Last year</option>
                                                        </select>
                                                </div>
                                        </div>
                                        <p className="hp-panel__note pg-telem-note">Driver searches only find drivers who have opted in to sharing their name.</p>
                                        <div className="pg-telem-actions">
                                                {isLoggedIn ? (
                                                        <label className="pg-switch">
                                                                <input type="checkbox" checked={myPrivateTelemetry} onChange={(e) => setMyPrivateTelemetry(e.target.checked)} />
                                                                Only my private telemetry
                                                        </label>
                                                ) : (
                                                        <button type="button" className="hp-link" onClick={() => navigate("/login")}>
                                                                Sign in to see your private telemetry
                                                        </button>
                                                )}
                                                <button type="submit" className="pg-btn" disabled={loading || !hasQuery}>
                                                        {loading ? "Searching…" : "Search telemetry"}
                                                </button>
                                        </div>
                                        {!hasQuery && <p className="pg-field__hint pg-telem-required">Fill in at least one field to search.</p>}
                                </form>

                                {error && (
                                        <section className="hp-panel pg-telem-error" role="alert">
                                                {error}
                                        </section>
                                )}

                                {data && (
                                        <section className="hp-panel">
                                                <PanelHeader title={`${data.totalSessions?.toLocaleString() ?? 0} sessions, ${data.totalLaps?.toLocaleString() ?? 0} laps`}>
                                                        <SegmentedControl
                                                                label="Comparison mode"
                                                                value={comparisonMode}
                                                                onChange={(mode) => {
                                                                        setComparisonMode(mode);
                                                                        if (mode === "fastest") setSelectedLaps([]);
                                                                }}
                                                                options={[
                                                                        { value: "fastest", content: "Compare to fastest" },
                                                                        { value: "select", content: "Pick 2 laps" },
                                                                ]}
                                                        />
                                                </PanelHeader>
                                                <p className="hp-panel__note">{lapHint}</p>

                                                {data.sessions && data.sessions.length > 0 ? (
                                                        <div className="pg-telem-sessions">
                                                                {data.sessions.map((session, sessionIdx) => (
                                                                        <article key={sessionIdx} className="pg-telem-session">
                                                                                <header className="pg-telem-session__head">
                                                                                        <h3 className="pg-telem-session__title">
                                                                                                {session.car} <span className="pg-muted">at</span> {session.track}
                                                                                        </h3>
                                                                                        <p className="pg-telem-session__meta">
                                                                                                <span>{session.driverName}</span>
                                                                                                {session.sessionTimestamp && <span>{new Date(session.sessionTimestamp).toLocaleString()}</span>}
                                                                                        </p>
                                                                                </header>
                                                                                <div className="pg-table-wrap">
                                                                                        <table className="pg-table pg-telem-laps">
                                                                                                <thead>
                                                                                                        <tr>
                                                                                                                <th>Lap</th>
                                                                                                                <th className="pg-num">Lap time</th>
                                                                                                                <th className="pg-num">Full throttle</th>
                                                                                                                <th className="pg-num">Max speed</th>
                                                                                                        </tr>
                                                                                                </thead>
                                                                                                <tbody>
                                                                                                        {session.laps &&
                                                                                                                session.laps.map((lap, lapIdx) => {
                                                                                                                        const selected = isLapSelected(lap, session);
                                                                                                                        const selectedIdx = getSelectedLapIndex(lap, session);
                                                                                                                        return (
                                                                                                                                <tr
                                                                                                                                        key={lapIdx}
                                                                                                                                        className={`pg-row-link${selected ? " is-selected" : ""}`}
                                                                                                                                        onClick={() => handleLapClick(lap, session)}
                                                                                                                                        aria-selected={comparisonMode === "select" ? selected : undefined}
                                                                                                                                >
                                                                                                                                        <td>
                                                                                                                                                {comparisonMode === "select" && <span className={`pg-pick${selected ? " is-on" : ""}`}>{selected ? selectedIdx + 1 : ""}</span>}
                                                                                                                                                {lap.lapNumber}
                                                                                                                                        </td>
                                                                                                                                        <td className="pg-num">{formatLapTime(lap.lapTime)}</td>
                                                                                                                                        <td className="pg-num">{lap.fullThrottlePercentage != null ? `${lap.fullThrottlePercentage.toFixed(1)}%` : "–"}</td>
                                                                                                                                        <td className="pg-num">{lap.maxSpeed != null ? lap.maxSpeed.toFixed(1) : "–"}</td>
                                                                                                                                </tr>
                                                                                                                        );
                                                                                                                })}
                                                                                                </tbody>
                                                                                        </table>
                                                                                </div>
                                                                        </article>
                                                                ))}
                                                        </div>
                                                ) : (
                                                        <p className="hp-panel__note">No sessions match your search. Try fewer filters or a longer time frame.</p>
                                                )}

                                                {hasMore && (
                                                        <div className="pg-pager">
                                                                <span>
                                                                        Showing {data.sessions.length} of {data.totalSessions} sessions
                                                                </span>
                                                                <button type="button" className="pg-pager__btn" onClick={handleLoadMore} disabled={loadingMore}>
                                                                        {loadingMore ? "Loading…" : "Load more sessions"}
                                                                </button>
                                                        </div>
                                                )}
                                        </section>
                                )}
                        </main>

                        {/* Fixed bar for lap selection mode */}
                        {comparisonMode === "select" && selectedLaps.length > 0 && (
                                <div className="pg-compare-bar">
                                        <div className="pg-compare-bar__slots">
                                                {renderSlot(selectedLaps[0], "Lap 1")}
                                                {renderSlot(selectedLaps[1], "Lap 2")}
                                        </div>
                                        <div className="pg-compare-bar__actions">
                                                <button type="button" className="pg-btn pg-btn--ghost" onClick={clearSelectedLaps}>
                                                        Clear
                                                </button>
                                                <button type="button" className="pg-btn" onClick={handleCompareSelected} disabled={selectedLaps.length !== 2}>
                                                        Compare laps
                                                </button>
                                        </div>
                                </div>
                        )}
                </div>
        );
};

export default TelemetryPage;
